import { Commit, Dispatch, GetterTree } from 'vuex';
import { CandidateStudyProgram } from '~/services/CSPService';
import { SchoolVerification } from '~/services/SchoolService';
import { ProfileImportResult } from '~/services/types/import';
import { CandidateProfileScorer } from '~/utils/candidate/CandidateProfileScorer';
import { CandidateStudyProgramHelper } from '~/utils/candidate/CandidateStudyProgramHelper';
import { awaitForValueWithTimeout } from '~/utils/promise/timeout';
import CandidateService, { Candidate, WorkAuthorization } from '~/services/CandidateService';
import IdentityService, { User } from '~/services/IdentityService';

export interface ProfileState {
    lastProfileImport: ProfileImportResult | null;
    candidate: Candidate | null;
    user: User | null;
    workAuthorizations: WorkAuthorization[] | null;
    schoolVerifications: SchoolVerification[] | null;
}

export type Getters = {
    getLastProfileImport: (state: ProfileState) => () => ProfileState['lastProfileImport'];
    getCandidate: (state: ProfileState) => () => ProfileState['candidate'];
    getUser: (state: ProfileState) => () => ProfileState['user'];
    getWorkAuthorizations: (state: ProfileState) => () => ProfileState['workAuthorizations'];
    getSchoolVerifications: (state: ProfileState) => () => ProfileState['schoolVerifications'];
    getStudyPrograms: (state: ProfileState) => () => CandidateStudyProgram[];
    awaitStudyPrograms: (state: ProfileState) => () => Promise<Candidate['studyPrograms']>;
    getSortedStudyPrograms: (_: ProfileState, getters: any) => () => CandidateStudyProgram[];
    awaitCandidate: (state: ProfileState) => () => Promise<ProfileState['candidate']>;
    awaitUser: (state: ProfileState) => () => Promise<ProfileState['user']>;
    awaitWorkAuthorizations: (
        state: ProfileState
    ) => () => Promise<ProfileState['workAuthorizations']>;
    awaitMostRelevantSchool: (state: ProfileState, getters: any) => () => Promise<number>;
    getCandidateProfileScorer: (state: ProfileState) => () => CandidateProfileScorer;
    awaitCandidateProfileScorer: (state: ProfileState) => () => Promise<CandidateProfileScorer>;
};

type GettersWithState = { [K in keyof Getters]: ReturnType<Getters[K]> };

export type ProfileModuleGetters = GetterTree<ProfileState, ProfileState>;

export default {
    namespaced: true,
    state: {
        lastProfileImport: null,
        candidate: null,
        user: null,
        workAuthorizations: null,
        schoolVerifications: null,
    } as ProfileState,
    getters: {
        getLastProfileImport: (state: ProfileState) => () => state.lastProfileImport,
        getCandidate: (state: ProfileState) => () => state.candidate,
        getUser: (state: ProfileState) => () => state.user,
        getWorkAuthorizations: (state: ProfileState) => () => state.workAuthorizations,
        getSchoolVerifications: (state: ProfileState) => () => state.schoolVerifications,
        getStudyPrograms: (state: ProfileState) => () =>
            [...((state.candidate && state.candidate.studyPrograms) || [])],
        getSortedStudyPrograms: (_: ProfileState, getters: GettersWithState) => () =>
            CandidateStudyProgramHelper.sortPrograms(getters.getStudyPrograms()),
        awaitStudyPrograms: (state: ProfileState) => () =>
            awaitForValueWithTimeout(
                3000,
                (state: ProfileState) => state.candidate?.studyPrograms
            )(state),
        awaitCandidate: (state: ProfileState) => () =>
            awaitForValueWithTimeout(3000, (state: ProfileState) => state.candidate)(state),
        awaitUser: (state: ProfileState) => () =>
            awaitForValueWithTimeout(3000, (state: ProfileState) => state.user)(state),
        awaitWorkAuthorizations: (state: ProfileState) => () =>
            awaitForValueWithTimeout(
                3000,
                (state: ProfileState) => state.workAuthorizations
            )(state),
        awaitMostRelevantSchool: (state: ProfileState, getters: any) => () => {
            return getters.awaitCandidate().then((candidate: Candidate) => {
                const schoolId = CandidateStudyProgramHelper.calculateSchoolIdOfMostRelevance(
                    candidate.studyPrograms
                );
                if (!schoolId) {
                    return null;
                }
                const schools = CandidateStudyProgramHelper.sortPrograms(
                    candidate.studyPrograms.filter(csp => csp.studyProgram.schoolId === schoolId)
                );
                if (schools.length === 0) {
                    return null;
                }
                return schools[0].studyProgram.school;
            });
        },
        getCandidateProfileScorer: (state: ProfileState) => () => {
            if (
                state.candidate === null ||
                state.user === null ||
                state.workAuthorizations === null
            ) {
                return null;
            }

            return new CandidateProfileScorer(
                state.candidate,
                state.user,
                state.workAuthorizations
            );
        },
        awaitCandidateProfileScorer: (state: ProfileState, getters: GettersWithState) => () => {
            return Promise.all([
                getters.awaitCandidate(),
                getters.awaitUser(),
                getters.awaitWorkAuthorizations(),
            ]).then(([candidate, user, workAuthorizations]) => {
                if ([candidate, user, workAuthorizations].some(arg => arg === null)) {
                    return null;
                }
                return new CandidateProfileScorer(candidate!, user!, workAuthorizations!);
            });
        },
    } as Getters,
    mutations: {
        setLastProfileImport(state: ProfileState, value: ProfileImportResult | null) {
            state.lastProfileImport = value;
        },
        setCandidate(state: ProfileState, candidate: Candidate | null) {
            state.candidate = CandidateStudyProgramHelper.cloneCandidateWithMergedPrograms(
                candidate,
                state.schoolVerifications
            );
        },
        setUser(state: ProfileState, user: User | null) {
            state.user = user;
        },
        setWorkAuthorizations(state: ProfileState, workAuthorizations: WorkAuthorization[] | null) {
            state.workAuthorizations = workAuthorizations;
        },
        setSchoolVerifications(
            state: ProfileState,
            schoolVerifications: SchoolVerification[] | null
        ) {
            state.schoolVerifications = schoolVerifications;
            if (!state.candidate) {
                return;
            }

            state.candidate = CandidateStudyProgramHelper.cloneCandidateWithMergedPrograms(
                state.candidate,
                state.schoolVerifications
            );
        },
        clear() {
            return {
                lastProfileImport: null,
                candidate: null,
                user: null,
                workAuthorizations: null,
                schoolVerifications: null,
            };
        },
    },
    actions: {
        initialize({ dispatch }: { dispatch: Dispatch }) {
            window.addEventListener('candidate:profile:update', () => {
                dispatch('updateProfile');
            });
        },
        async updateProfile({ commit }: { commit: Commit }) {
            const loadUser = (): Promise<User> => {
                const service = new IdentityService();
                return service.getMe();
            };

            const loadCandidate = (): Promise<Candidate> => {
                const service = new CandidateService();
                return service.getMe();
            };

            const loadWorkAuthorizations = (): Promise<WorkAuthorization[]> => {
                const service = new CandidateService();
                return service.getWorkAuthorizations();
            };

            const loadLastProfileImport = (): Promise<ProfileImportResult | null> => {
                const service = new CandidateService();
                return service.getLastProfileImport();
            };

            Promise.all([
                loadUser(),
                loadCandidate(),
                loadWorkAuthorizations(),
                loadLastProfileImport(),
                // this.loadSchoolVerifications(true),
            ]).then(([user, candidate, workAuthorizations, importResult]) => {
                commit('setUser', user);
                commit('setCandidate', candidate);
                commit('setWorkAuthorizations', workAuthorizations);
                commit('setLastProfileImport', importResult);

                window.dispatchEvent(new CustomEvent('candidate:profile:loaded'));
            });
        },
    },
};
